import { Component, computed, signal, effect, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { ApiService, ProductCategory, Product as ApiProduct, Table } from '../services/api.service';
import { QrStateService } from '../services/qr-state.service';
import { TableSelectModalComponent } from '../components/table-select-modal.component';

interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  // Remaining stock; null = not tracked (unlimited)
  stock: number | null;
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, RouterLink, TableSelectModalComponent],
  template: `
    <main class="min-h-screen pb-24 bg-gray-950 text-gray-100">
    
      <!-- HERO -->
      <section class="px-4">
        <div class="relative rounded-2xl overflow-hidden">
          <!-- <img src="/assets/hero-restaurant.jpg" alt="Hero" class="h-48 w-full object-cover"> -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10"></div>
          <div class="absolute inset-0 p-4 flex flex-col justify-end">
            <h1 class="text-2xl font-semibold">Bestelle dein Essen schnell & einfach</h1>
            <p class="text-sm text-gray-300">Frisch zubereitet. Direkt an den Tisch.</p>
            <div class="mt-3">
              <a href="#menu" class="inline-flex items-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400">Jetzt
              bestellen</a>
            </div>
          </div>
        </div>
      </section>
    
      <!-- Table picker bar -->
      <section class="px-4 mt-3">
        <div class="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 px-3 py-2">
          <div class="flex items-center gap-3">
            <div class="text-sm space-y-1">
              <div class="font-medium flex items-center gap-3">
                <span>{{ qrState.tableNumber() ? ('Tisch ' + qrState.tableNumber()) : (qrState.tableId() ? 'Tisch …' : 'Tisch wählen') }}</span>
                @if (qrState.isWaiterMode()) {
                  <span
                  class="inline-flex items-center rounded-full bg-blue-600/20 border border-blue-500/50 px-2.5 py-0.5 text-[10px] font-medium text-blue-300">Bedienung</span>
                }
              </div>
              @if (!qrState.isWaiterMode()) {
                <div class="text-gray-400 text-xs">
                  {{ qrState.hasQrData() ? 'Automatisch über QR-Code gesetzt' : 'Für den Service am Platz' }}
                </div>
              }
              @if (qrState.isWaiterMode() && !qrState.tableId()) {
                <div class="text-orange-400 text-xs">Bitte Tisch für die Bestellung wählen</div>
              }
            </div>
          </div>
          @if (qrState.isWaiterMode() || !qrState.tableId()) {
            <button class="rounded-md bg-gray-800 px-3 py-1.5 text-sm" (click)="promptTable()">{{ qrState.tableId() ? 'Ändern' : 'Festlegen' }}</button>
          }
        </div>
      </section>
    
      <!-- Menu -->
      <section id="menu" class="px-4 mt-4">
        <h2 class="text-lg font-semibold">Speisekarte</h2>
        <p class="text-sm text-gray-400">Wähle eine Kategorie</p>
    
        <!-- Loading state -->
        @if (loading()) {
          <div class="text-center py-8">
            <div class="text-gray-400">Lade Speisekarte...</div>
          </div>
        }
    
        <!-- Error state -->
        @if (error()) {
          <div class="bg-red-900/50 border border-red-800 rounded-xl p-4 mt-3">
            <div class="text-red-300">{{ error() }}</div>
          </div>
        }
    
        <!-- Tabs -->
        @if (!loading()) {
          <div class="relative mt-3">
            <div class="flex gap-2 overflow-x-auto rounded-xl bg-gray-900 p-1 border border-gray-800">
              @for (c of categoryNames(); track c) {
                <button
                  (click)="activeCat.set(c)"
                  class="shrink-0 rounded-md px-4 py-2 text-sm whitespace-nowrap"
                  [class.bg-gray-100]="activeCat()===c" [class.text-black]="activeCat()===c">
                  {{ c }}
                </button>
              }
            </div>
          </div>
        }
    
        <!-- Product grid - 2 columns -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          @for (p of filtered(); track p.id) {
            <article class="rounded-xl overflow-hidden border border-gray-800 bg-gray-900">
              @if (p.image) {
                <img [src]="p.image" [alt]="p.name" class="h-36 w-full object-cover">
              }
              <div class="p-3">
                <div class="flex items-center justify-between text-sm">
                  <div>
                    <div class="font-medium flex items-center gap-2">
                      <span>{{ p.name }}</span>
                      @if (qrState.isWaiterMode() && lowStock(p) !== null) {
                        <span class="shrink-0 rounded-full px-2 py-0.5 text-xs bg-orange-500/15 text-orange-400">Noch {{ lowStock(p) }}</span>
                      }
                    </div>
                    <div class="text-gray-400 text-xs">{{ p.category }}</div>
                  </div>
                  <div class="font-semibold">{{ p.price | currency:'EUR':'symbol':'1.2-2' }}</div>
                </div>
                <!-- Add bar -->
                <div class="mt-3 flex items-center gap-2">
                  @if (qtyFor(p) === 0) {
                    <button
                      class="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-orange-500 px-3 py-2 text-sm font-medium text-white hover:bg-orange-400"
                      (click)="add(p)">
                      <span class="text-lg leading-none">+</span>
                      Hinzufügen
                    </button>
                  }
                  @if (qtyFor(p) > 0) {
                    <div class="flex-1 flex items-center justify-between rounded-md border border-gray-800 bg-gray-950 p-1">
                      <button class="grid place-items-center w-9 h-8 rounded-md bg-gray-800 text-lg leading-none hover:bg-gray-700" (click)="remove(p)">−</button>
                      <span class="text-sm font-medium">{{ qtyFor(p) }}</span>
                      <button class="grid place-items-center w-9 h-8 rounded-md bg-orange-500 text-lg leading-none text-white hover:bg-orange-400" (click)="add(p)">+</button>
                    </div>
                  }
                  <button class="grid place-items-center rounded-md border border-gray-800 p-2" (click)="note(p)">
                    <span class="material-symbols-outlined text-base">chat</span>
                  </button>
                </div>
              </div>
            </article>
          }
        </div>
      </section>
    
      <!-- Cart bar -->
      @if (count() > 0) {
        <div class="fixed bottom-4 left-4 right-4 z-50">
          <div class="rounded-xl border border-gray-800 bg-gray-900 px-3 py-3 shadow-lg flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 text-sm">
              <span class="material-symbols-outlined text-xl">shopping_cart</span>
              <div>
                <div class="font-medium">{{ count() }} Artikel</div>
                <div class="text-gray-400">{{ total() | currency:'EUR':'symbol':'1.2-2' }} gesamt</div>
              </div>
            </div>
            <a routerLink="/checkout"
              class="shrink-0 inline-flex items-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400">Zur
            Kasse</a>
          </div>
        </div>
      }
    
      <!-- Table Selection Modal -->
      @if (showTableModal()) {
        <app-table-select-modal
          [tables]="availableTables()"
          [selectedTableId]="qrState.tableId()"
          (tableSelected)="selectTable($event)"
          (closed)="closeTableModal()" />
      }
    </main>
    `,
})
export class MenuComponent implements OnInit {
  private apiService = inject(ApiService);
  qrState = inject(QrStateService);
  private route = inject(ActivatedRoute);
  private hiddenCategoryNames = new Set(['getränke', 'getraenke', 'essen']);
  // Dynamic products from API
  private all = signal<Product[]>([]);
  private categories = signal<ProductCategory[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  activeCat = signal<string>('');
  categoryNames = computed(() => this.categories().map(c => c.name).filter(name => !this.isHiddenCategory(name)));
  filtered = computed(() => this.all().filter(p => p.category === this.activeCat()));

  // Cart state (enriched with display data for checkout)
  private cart = signal<Record<string, { qty: number; price: number; name: string; image: string; note?: string }>>(
    (() => {
      try {
        const raw = localStorage.getItem('cart');
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {} as Record<string, { qty: number; price: number; name: string; image: string; note?: string }>;
      }
    })()
  );
  count = computed(() => Object.values(this.cart()).reduce((a, b) => a + b.qty, 0));
  total = computed(() => Object.values(this.cart()).reduce((a, b) => a + b.qty * b.price, 0));

  availableTables = signal<Table[]>([]);
  showTableModal = signal<boolean>(false);

  ngOnInit() {
    // Initialize QR state from URL
    this.qrState.initializeFromUrl(this.route);

    // Load menu data if we have event ID
    if (this.qrState.eventId()) {
      this.loadMenuData(this.qrState.eventId()!);
      this.loadAvailableTables(this.qrState.eventId()!);
    } else {
      // No event ID → show error
      this.error.set('Kein Event-Kontext gefunden. Bitte QR-Code scannen.');
    }
  }

  private async loadMenuData(eventId: string) {
    this.loading.set(true);
    this.error.set(null);
    console.log('Loading menu data for eventId:', eventId);

    try {
      // Load categories and products separately (required)
      const [categories, products] = await Promise.all([
        this.apiService.getEventCategories(eventId).toPromise(),
        this.apiService.getEventProducts(eventId).toPromise()
      ]);

      console.log('API Results:', { categories, products });

      if (!categories || categories.length === 0) {
        console.warn('No categories received from API');
        this.error.set('Keine Kategorien gefunden');
        return;
      }

      this.categories.set(categories);
      const visibleNames = categories.map(c => c.name).filter(name => !this.isHiddenCategory(name));
      this.activeCat.set(visibleNames[0] || categories[0].name);

      if (!products || products.length === 0) {
        console.warn('No products received from API');
        this.error.set('Keine Produkte gefunden');
        return;
      }

      // Sold-out / disabled products are not offered to guests
      const mappedProducts: Product[] = products.filter((p: ApiProduct) => p.isAvailable).map((p: ApiProduct) => {
        const category = categories.find(c => c.id === p.categoryId);
        return {
          id: p.id,
          name: p.name,
          price: p.price,
          image: this.apiService.getProductImageUrl(p),
          category: category?.name || 'Unbekannt',
          stock: p.stock ?? null
        };
      });
      this.all.set(mappedProducts);

    } catch (err) {
      console.error('Failed to load menu data:', err);
      this.error.set('Fehler beim Laden der Speisekarte');
    } finally {
      this.loading.set(false);
    }
  }

  private async loadAvailableTables(eventId: string) {
    try {
      const tables = await this.apiService.getAllTables(eventId).toPromise();
      this.availableTables.set(tables ?? []);

      // Resolve the display number for a table that was set via QR code (id only)
      const tableId = this.qrState.tableId();
      if (tableId && !this.qrState.tableNumber()) {
        const current = (tables ?? []).find(t => t.id === tableId);
        if (current) {
          this.qrState.setTableNumber(current.number);
        }
      }
    } catch (err) {
      console.error('Failed to load available tables:', err);
    }
  }

  private isHiddenCategory(name: string) {
    return this.hiddenCategoryNames.has(name.trim().toLowerCase());
  }

  // persist cart and count for checkout page
  _persist = effect(() => {
    const snapshot = this.cart();
    try {
      localStorage.setItem('cartCount', String(this.count()));
      localStorage.setItem('cart', JSON.stringify(snapshot));
    } catch {}
  });

  promptTable() {
    this.showTableModal.set(true);
  }

  selectTable(table: Table) {
    this.qrState.selectTable(table.id, table.number);
    this.showTableModal.set(false);
  }

  closeTableModal() {
    this.showTableModal.set(false);
  }

  // Remaining stock when tracked and running low (< 10), else null
  lowStock(p: Product): number | null {
    if (p.stock === null) return null;
    return p.stock < 10 ? p.stock : null;
  }

  add(p: Product) {
    // Never put more in the cart than the tracked stock allows
    if (p.stock !== null && this.qtyFor(p) + 1 > p.stock) return;
    // Standard-Add ohne Notiz: key = id::
    const key = `${p.id}::`;
    const c = { ...this.cart() };
    c[key] = c[key]
      ? { ...c[key], qty: c[key].qty + 1 }
      : { qty: 1, price: p.price, name: p.name, image: p.image, note: '' };
    this.cart.set(c);
  }

  // Gesamtmenge eines Produkts über alle Warenkorb-Einträge (inkl. Notiz-Varianten)
  qtyFor(p: Product): number {
    return Object.entries(this.cart())
      .filter(([key]) => key.startsWith(`${p.id}::`))
      .reduce((sum, [, item]) => sum + item.qty, 0);
  }

  remove(p: Product) {
    const c = { ...this.cart() };
    const plainKey = `${p.id}::`;
    // Bevorzugt den Eintrag ohne Notiz reduzieren, sonst eine Notiz-Variante
    const key = c[plainKey] ? plainKey : Object.keys(c).find(k => k.startsWith(plainKey));
    if (!key || !c[key]) return;
    if (c[key].qty > 1) {
      c[key] = { ...c[key], qty: c[key].qty - 1 };
    } else {
      delete c[key];
    }
    this.cart.set(c);
  }

  note(p: Product) {
    const raw = prompt('Notiz für ' + p.name + ' (optional)');
    if (raw === null) return;
    if (raw === undefined) return; // Abgebrochen
    const text = raw.trim();
    if (!text) {
      // leere Eingabe => wie normales Hinzufügen ohne Notiz
      return this.add(p);
    }
    // Never put more in the cart than the tracked stock allows
    if (p.stock !== null && this.qtyFor(p) + 1 > p.stock) return;
    const key = `${p.id}::${text}`;
    const c = { ...this.cart() };
    c[key] = c[key]
      ? { ...c[key], qty: c[key].qty + 1 }
      : { qty: 1, price: p.price, name: p.name, image: p.image, note: text };
    this.cart.set(c);
  }
}
